import { IonButton, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import classNames from 'classnames';
import { pencil } from 'ionicons/icons';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DqText } from 'src/components/dq-text';
import { JobMessages } from 'src/intl/JobMessages';
import type { EmailGuestShare, JobLogPermission } from 'src/models/jobLog';
import { useBreakpoint } from 'src/utils/helpers/platformHelpers';
import { GenericMessages } from '../../../intl/GenericMessages';
import { filterOutExpiredEmailGuestShares, filterOutExpiredPermissions } from '../../../utils/logs/logHelpers';

interface JobSummaryDetailsProps {
  showTimeDetails: boolean;
  updateJobDetails?: () => void;
  showUpdateJobButton: boolean;
  showEstimatedTimeToCompletion: boolean;

  tubesCleaned?: string;
  totalTubes?: string;
  timeInTube?: string;
  activeRunTime?: string;
  controller?: string;
  siteName?: string;
  modelNumber?: string;
  description?: string;
  fileName?: string;
  lanceSpeed?: string;
  estimatedTimeToCompletion?: string;
  createdAt?: number;
  updatedAt?: number;
  timezone: string;
  permissions?: JobLogPermission[];
  emailGuestShares?: EmailGuestShare[];
  startTime?: string;
  onReportSharedWithClicked?: () => void;
  canSeeReportSharedWith: boolean;
}

export const JobSummary: FC<JobSummaryDetailsProps> = props => {
  const intl = useIntl();
  const isMd = useBreakpoint('md');
  const [didSetExpanded, setExpanded] = React.useState(false);
  const isExpanded = isMd || didSetExpanded;
  const { formatMessage } = useIntl();
  const onReportSharedWithClicked = props.onReportSharedWithClicked ?? (() => {});
  const canSeeReportSharedWith = props.canSeeReportSharedWith;

  const sharedWith = useMemo(() => {
    const permissions = filterOutExpiredPermissions(props.permissions);
    const emailGuestShares = filterOutExpiredEmailGuestShares(props.emailGuestShares);
    const numberOfShares = permissions.length + emailGuestShares.length;
    if (numberOfShares <= 0) {
      return formatMessage(JobMessages.jobInfoSharedWithNone);
    }

    const shareName = permissions.length > 0 ? permissions[0].principalName : emailGuestShares[0].emailAddress;

    return numberOfShares === 1 ? `${shareName}` : `${shareName}, + ${numberOfShares - 1}`;
  }, [formatMessage, props.emailGuestShares, props.permissions]);

  return (
    <IonGrid className="job-summary-list">
      <IonRow>
        <IonCol size-xs="6" size-md="3" className="o1">
          <DqText variant="body-strong">{(props.tubesCleaned || '0') + '/' + (props.totalTubes || '0')}</DqText>
          <DqText variant="body-small">{formatMessage(JobMessages.tubesCleanedLabel)}</DqText>
        </IonCol>
        {props.showTimeDetails && (
          <IonCol size="6" sizeMd="3" className={classNames(isMd && 'o1', !isExpanded && !isMd && 'ion-hide')}>
            <DqText variant="body-strong">
              {props.activeRunTime || formatMessage(JobMessages.jobInfoNotProvided)}
            </DqText>
            <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryActiveRunTime)}</DqText>
          </IonCol>
        )}
        {props.showTimeDetails && (
          <IonCol size-xs="6" size-md="3" className={classNames(isMd && 'o1', !isExpanded && !isMd && 'ion-hide')}>
            <DqText variant="body-strong">{props.timeInTube || formatMessage(JobMessages.jobInfoNotProvided)}</DqText>
            <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryTimeInTube)}</DqText>
          </IonCol>
        )}
        {/* If estimated time to completion should be shown, render it here w/ edit button, and show controller in next column
            If not, show controller info here, and do not render the other controller column.
         */}
        <IonCol size-xs="6" size-md="3" className="o2">
          <div className="job-summary-button-wrapper">
            <DqText variant="body-strong" className="job-summary-button-text">
              {(props.showEstimatedTimeToCompletion ? props.estimatedTimeToCompletion : props.controller) ||
                formatMessage(JobMessages.jobInfoNotProvided)}
            </DqText>
            {props.showUpdateJobButton && (
              <IonButton
                className="job-summary-button"
                onClick={props.updateJobDetails}
                fill="clear"
                size="small"
                color="dark"
              >
                <IonIcon slot="icon-only" icon={pencil} />
              </IonButton>
            )}
          </div>
          <DqText variant="body-small">
            {props.showEstimatedTimeToCompletion
              ? formatMessage(JobMessages.jobInfoEstimatedTimeToCompletionLabel)
              : formatMessage(JobMessages.jobInfoSummaryController)}
          </DqText>
        </IonCol>
        {props.showEstimatedTimeToCompletion && (
          <IonCol size-xs="6" size-md="3" className={classNames(!isExpanded && 'ion-hide')}>
            <DqText variant="body-strong">{props.controller || formatMessage(JobMessages.jobInfoNotProvided)}</DqText>
            <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryController)}</DqText>
          </IonCol>
        )}
        <IonCol size-xs="6" size-md="3" className={classNames(!isExpanded && 'ion-hide')}>
          <DqText variant="body-strong">{props.siteName || formatMessage(JobMessages.jobInfoNotProvided)}</DqText>
          <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummarySiteName)}</DqText>
        </IonCol>
        <IonCol size-xs="6" size-md="3" className={classNames(!isExpanded && 'ion-hide')}>
          <DqText variant="body-strong">{props.modelNumber || formatMessage(JobMessages.jobInfoNotProvided)}</DqText>
          <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryModelNumber)}</DqText>
        </IonCol>
        <IonCol size-xs="6" size-sm="3" className={classNames(!isExpanded && 'ion-hide')}>
          <DqText variant="body-strong">{props.lanceSpeed || formatMessage(JobMessages.jobInfoNotProvided)}</DqText>
          <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryLanceSpeedLabel)}</DqText>
        </IonCol>
        <IonCol size-xs="6" size-sm="3" className={classNames(!isExpanded && 'ion-hide')}>
          <DqText variant="body-strong">{props.description || formatMessage(JobMessages.jobInfoNotProvided)}</DqText>
          <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryDescription)}</DqText>
        </IonCol>
        <IonCol size-xs="6" size-sm="3" className={classNames(!isExpanded && 'ion-hide')}>
          <DqText variant="body-strong">
            {props.updatedAt
              ? new Intl.DateTimeFormat(intl.locale, {
                  timeZone: props.timezone,
                  dateStyle: 'short',
                  timeStyle: 'long',
                }).format(props.updatedAt)
              : props.createdAt
                ? new Intl.DateTimeFormat(intl.locale, {
                    timeZone: props.timezone,
                    dateStyle: 'short',
                    timeStyle: 'long',
                  }).format(props.createdAt)
                : formatMessage(GenericMessages.unknownPlaceholder)}
          </DqText>
          <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSummaryLastUpdatedLabel)}</DqText>
        </IonCol>
        {props.startTime && (
          <IonCol size-xs="6" size-sm="3">
            <DqText variant="body-strong">{props.startTime}</DqText>
            <DqText variant="body-small">{formatMessage(JobMessages.startTime)}</DqText>
          </IonCol>
        )}
        {canSeeReportSharedWith && (
          <IonCol size-xs="6" size-sm="3">
            <DqText variant="body-strong-underlined-clickable" onClick={onReportSharedWithClicked}>
              {sharedWith}
            </DqText>
            <DqText variant="body-small">{formatMessage(JobMessages.jobInfoSharedWithLabel)}</DqText>
          </IonCol>
        )}
      </IonRow>
      {!isMd && (
        <IonButton fill="clear">
          <DqText variant="body-small" color="secondary" onClick={() => setExpanded(c => !c)}>
            {formatMessage(isExpanded ? JobMessages.jobInfoSummaryViewLess : JobMessages.jobInfoSummaryViewMore)}
          </DqText>
        </IonButton>
      )}
    </IonGrid>
  );
};
